import { useForm } from "react-hook-form";
import { formValues, schema } from "./validations";
import { NOTE } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getRequiredFields,
  planStatus,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { toast } from "@/components";
import { useAddNoteMutation, useGetNoteList } from "@/services";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/routes/utils";
import { useMemo, useState } from "react";

const useNotes = () => {
  const {id, action, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("pm");
  const form = useForm<formValues>({
    defaultValues: {
      [NOTE]: "",
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);
  const {taskData, planData} = useSelector((state: RootState) => state.currentData)

  const {data: noteList, isLoading: isLoadingNote} = useGetNoteList({
    taskId: taskData?.taskId,
    projectId: id as string
  });

  const isPlanApproved = useMemo(() => planData?.status == planStatus.approved.value, [planData]);
  
  const { mutate: addInfo, isPending: isAddPending } =
    useAddNoteMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ["note-list", taskData?.taskId]})
        form.reset({
          [NOTE]: ""
        })
        toast({ message: "Notes Added Successfully" });
      },
    });

  const onSubmit = (data: formValues) => {
    const _data = setEmptyFieldsToNull(removeEmptyFields(data));

    addInfo({projectId: id as string, taskId: taskData?.taskId, data: {..._data}})
  };

  const onChangeTab = (currentTab: string) => {
    setActiveTab(currentTab)
  }

  const noteListByRole = useMemo(() => {
    if (activeTab == "pm") {
      return noteList?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "project manager")
    } else if (activeTab == "sm") {
      return noteList?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "site manager")
    } else if (activeTab == "qc") {
      return noteList?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "quality officer")
    } else if (activeTab == "sc") {
      return noteList?.data?.filter((data: any) => data?.createdByRoleName?.toLowerCase() == "safety officer")
    }
    return [];
  }, [activeTab, noteList]);

  return {
    form,
    requiredFields,
    onSubmit,
    isAddPending,
    isLoadingNote,
    noteList,
    isView,
    activeTab,
    onChangeTab,
    noteListByRole: noteListByRole || [],
    isPlanApproved
  };
};

export default useNotes;
